// Single source of truth for which add-on the "Video Call" option posts.
// The backend resolves the video provider from this product id
// (VIDEO_PROVIDER_MAP): "video-addon" -> tokbox (v1, REMOVED),
// "video-addon-v2" -> zoom (v2), "video-addon-v3" -> realtimekit (v3).
//
// tokbox (OpenTok/Vonage) was removed — the Vonage Video account is paused and
// its OpenTok implementation was deleted from the frontend. The purchasable
// in-app video call now uses v3 (RealtimeKit).
export const VIDEO_CALL_ADDON_ID = "video-addon-v3";

// Audio-only variant of the v3 (RealtimeKit) call: same duration, cheaper.
// Same provider/meeting — the backend swaps to AUDIO_ROOM presets at token time
// based on consultation.callType.
export const AUDIO_CALL_ADDON_ID = "audio-addon-v3";

// Selectable in-app video add-on versions, newest first. Used by the extend
// flow to pick which version to re-purchase.
export const VIDEO_ADDON_VERSIONS = [
  { id: "video-addon-v3", provider: "realtimekit" },
  { id: "video-addon-v2", provider: "zoom" },
];

// consultation.videoProvider -> the add-on id that provisions that provider.
export const PROVIDER_TO_ADDON = {
  realtimekit: "video-addon-v3",
  zoom: "video-addon-v2",
};

// Decide which call add-on version an extend should buy.
//   0. audio consultations re-buy the audio add-on while it's available;
//   1. else keep the consultation's current video version if it's available;
//   2. else switch to the newest available version (may downgrade);
//   3. else mark unavailable (caller shows a disabled state + remove hint).
// Audio is deliberately NOT in VIDEO_ADDON_VERSIONS — the newest-first fallback
// must never silently downgrade a video consultation to audio.
// Availability mirrors the backend /pay check: the consultant must accept
// calls AND the version's own `available` flag (from /status prices) must be set.
export function resolveExtendVideoVersion(consultation, prices, acceptCall) {
  const isAvail = (id) => !!acceptCall && prices?.[id]?.available === true;

  if (consultation?.callType === "audio" && isAvail(AUDIO_CALL_ADDON_ID)) {
    return {
      versionId: AUDIO_CALL_ADDON_ID,
      provider: "realtimekit",
      callType: "audio",
      available: true,
      downgraded: false,
    };
  }

  const currentId = PROVIDER_TO_ADDON[consultation?.videoProvider];

  if (currentId && isAvail(currentId)) {
    const v = VIDEO_ADDON_VERSIONS.find((x) => x.id === currentId);
    return {
      versionId: v.id,
      provider: v.provider,
      callType: "video",
      available: true,
      // Audio consult whose audio add-on went unavailable falls back to the
      // video version — price goes UP; surface it as a change in the cart.
      downgraded: consultation?.callType === "audio",
    };
  }

  const newest = VIDEO_ADDON_VERSIONS.find((x) => isAvail(x.id));
  if (newest) {
    return {
      versionId: newest.id,
      provider: newest.provider,
      callType: "video",
      available: true,
      downgraded: !!currentId,
    };
  }

  const fallback =
    VIDEO_ADDON_VERSIONS.find((x) => x.id === currentId) || VIDEO_ADDON_VERSIONS[0];
  return {
    versionId: fallback.id,
    provider: fallback.provider,
    callType: "video",
    available: false,
    downgraded: false,
  };
}
